/**
 * Browser file downloads, done the way that actually works across browsers.
 *
 * The naive pattern — create an <a>, set href/download, call click() — fails in
 * two common ways that both present as "nothing downloads" (DEF: Export CSV does
 * not produce a file):
 *   1. Clicking an anchor that was never added to the document does nothing in
 *      several browsers (Firefox, some Chromium security contexts). The anchor
 *      must be in the DOM.
 *   2. Revoking the object URL synchronously after click() can race the download
 *      and revoke the blob before the browser has read it, so no file appears.
 *      Cleanup has to be deferred.
 *
 * These helpers do both correctly, so every caller gets a reliable download.
 */

/** Trigger a download for a Blob. */
export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);   // must be in the DOM for click() to fire
  a.click();
  // Defer cleanup so the download has started before the URL is revoked.
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Download text as a UTF-8 CSV. Prefixes a BOM so Excel opens it with the right
 * encoding (accented names, the en-dash, etc. don't turn into mojibake).
 */
export function downloadCsv(filename, text) {
  downloadBlob(filename, new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8;' }));
}

/**
 * Build CSV text from a header row + data rows, quoting/escaping every cell so
 * commas, quotes and newlines inside values can't break the columns.
 *
 * @param {string[]} header column titles
 * @param {Array<Array<*>>} rows one array of cell values per record
 */
export function toCsv(header, rows) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
}
